/*----------------------------------
- NEWSLETTER SUBSCRIBE HOOK
----------------------------------*/

/*----------------------------------
- DEPENDENCIES
----------------------------------*/

import type { EditionPersona } from '@uniquedomains/components/contracts/editions/public';
import { useRef, useState } from 'preact/hooks';

import { appErrorReporter } from '@/common/libs/errorReporting';
import { NEWSLETTER_SUBSCRIBE_GATEWAY_PATH } from '@/common/contracts/newsletterGateway';
import {
    isValidNewsletterEmail,
    NEWSLETTER_HONEYPOT_FIELD,
    type NewsletterPublicSubscriptionSource,
    type NewsletterSubscribeResponse,
} from '@uniquedomains/components/contracts/newsletter/public';

/*----------------------------------
- TYPES
----------------------------------*/

export type NewsletterFormStatus =
    | 'idle'
    | 'submitting'
    | 'subscribed'
    | 'invalid'
    | 'too_fast'
    | 'rate_limited'
    | 'error';

type UseNewsletterSubscribe = {
    email: string;
    setEmail: (value: string) => void;
    honeypot: string;
    setHoneypot: (value: string) => void;
    status: NewsletterFormStatus;
    submit: () => Promise<void>;
};

/*----------------------------------
- HOOK
----------------------------------*/

/**
 * Owns the subscribe request and the timing signal the gateway checks. Only the
 * elapsed delta crosses the wire, so a skewed client clock cannot trip the check.
 */
export const useNewsletterSubscribe = (
    source: NewsletterPublicSubscriptionSource,
    /**
     * Runs once the list accepted the address. The hook itself never tracks: whatever the
     * caller records must not carry the address, so the caller names only what it knows.
     */
    onSubscribed?: () => void,
    /** The editions band's persona choice, sent only when the form asks it (ADR-0074). */
    persona?: EditionPersona,
): UseNewsletterSubscribe => {
    const [email, setEmail] = useState('');
    const [honeypot, setHoneypot] = useState('');
    const [status, setStatus] = useState<NewsletterFormStatus>('idle');
    const mountedAtRef = useRef(Date.now());

    const submit = async (): Promise<void> => {
        if (status === 'submitting') return;

        const normalized = email.trim().toLowerCase();
        if (!isValidNewsletterEmail(normalized)) {
            setStatus('invalid');
            return;
        }

        setStatus('submitting');
        try {
            const httpResponse = await window.fetch(NEWSLETTER_SUBSCRIBE_GATEWAY_PATH, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    email: normalized,
                    source,
                    ...(persona ? { persona } : {}),
                    elapsedMs: Date.now() - mountedAtRef.current,
                    [NEWSLETTER_HONEYPOT_FIELD]: honeypot,
                }),
            });
            const payload = (await httpResponse.json()) as NewsletterSubscribeResponse;

            if (payload.status === 'subscribed') {
                setStatus('subscribed');
                onSubscribed?.();
            }
            else if (payload.status === 'invalid_email') setStatus('invalid');
            else if (payload.status === 'too_fast') setStatus('too_fast');
            else if (payload.status === 'rate_limited') setStatus('rate_limited');
            else setStatus('error');
        } catch (error) {
            appErrorReporter.app.handleError(error);
            setStatus('error');
        }
    };

    return { email, setEmail, honeypot, setHoneypot, status, submit };
};
